import { useCallback, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { writeTextToClipboard } from "@/utils/clipboard";
import {
  formatFreelancerCopyText,
  formatFreelancersCopyText,
  type FreelancerCopyData,
} from "@/utils/freelancer-clipboard";

export function useCopyFreelancerDetails() {
  const { toast } = useToast();
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [fallbackText, setFallbackText] = useState("");

  const copyDetails = useCallback(
    async (data: FreelancerCopyData) => {
      const text = formatFreelancerCopyText(data);
      if (!text) {
        toast({
          variant: "destructive",
          title: "Nada para copiar",
          description: "Não há dados do freelancer para copiar.",
        });
        return;
      }
      try {
        await writeTextToClipboard(text);
        toast({
          title: "Copiado!",
          description: "Detalhes do freelancer copiados para a área de transferência.",
        });
      } catch (err) {
        toast({
          variant: "destructive",
          title: "Erro ao copiar",
          description:
            (err as Error).message ||
            "Não foi possível copiar automaticamente.",
        });
        setFallbackText(text);
        setFallbackOpen(true);
      }
    },
    [toast],
  );

  const copyMultipleDetails = useCallback(
    async (list: FreelancerCopyData[]) => {
      const text = formatFreelancersCopyText(list);
      if (!text) {
        toast({
          variant: "destructive",
          title: "Nada para copiar",
          description: "Não há dados de freelancers para copiar.",
        });
        return;
      }
      try {
        await writeTextToClipboard(text);
        toast({
          title: "Copiado!",
          description: `${list.length} candidato${list.length > 1 ? "s" : ""} copiado${list.length > 1 ? "s" : ""} para a área de transferência.`,
        });
      } catch (err) {
        toast({
          variant: "destructive",
          title: "Erro ao copiar",
          description:
            (err as Error).message ||
            "Não foi possível copiar automaticamente.",
        });
        setFallbackText(text);
        setFallbackOpen(true);
      }
    },
    [toast],
  );

  return {
    copyDetails,
    copyMultipleDetails,
    fallbackOpen,
    setFallbackOpen,
    fallbackText,
  };
}
